import type { ReactNode } from "react";
import { Fragment } from "react";

/**
 * Inline-markup renderer for copy that carries a little formatting, e.g.
 * `"The <span>largest beauty marketplace</span> for women."`
 *
 * Extracted from WorldLida so the *server* can use it too: it is a plain
 * module, not a Client Component, so message catalogs and the privacy-policy
 * document both render their markup during SSR. Only presentational inline
 * tags are supported — no hrefs, no handlers, nothing that can break a page.
 */

// ============================================================
// HTML parsing — small, recursive, depth-bounded
// ============================================================

type InlineTag =
  | 'span'
  | 'em'
  | 'strong'
  | 'b'
  | 'i'
  | 'u'
  | 'small'
  | 'mark';

type ParsedNode =
  | { type: 'text'; content: string }
  | { type: 'br' }
  | { type: InlineTag; children: ParsedNode[] };

const INLINE_TAGS = new Set<InlineTag>([
  'span',
  'em',
  'strong',
  'b',
  'i',
  'u',
  'small',
  'mark',
]);

const MAX_PARSE_DEPTH = 6;

const ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

function decodeEntities(s: string): string {
  return s.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (raw, code: string) => {
    if (code[0] === '#') {
      const n =
        code[1] === 'x' || code[1] === 'X'
          ? parseInt(code.slice(2), 16)
          : parseInt(code.slice(1), 10);
      return Number.isFinite(n) ? String.fromCodePoint(n) : raw;
    }
    return ENTITIES[code] ?? raw;
  });
}

function stripTags(s: string): string {
  return s.replace(/<[^>]*>/g, '');
}

const NODE_RE_SRC =
  '<\\s*(br)\\s*\\/?\\s*>|<\\s*([a-zA-Z][a-zA-Z0-9-]*)\\b[^>]*>([\\s\\S]*?)<\\s*\\/\\s*\\2\\s*>|([^<]+)';

function parseHtmlString(input: string, depth = 0): ParsedNode[] {
  const out: ParsedNode[] = [];
  if (!input) return out;
  const re = new RegExp(NODE_RE_SRC, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(input)) !== null) {
    if (m[1]) {
      out.push({ type: 'br' });
    } else if (m[2]) {
      const tag = m[2].toLowerCase();
      const inner = m[3] ?? '';
      if (INLINE_TAGS.has(tag as InlineTag) && depth < MAX_PARSE_DEPTH) {
        out.push({
          type: tag as InlineTag,
          children: parseHtmlString(inner, depth + 1),
        });
      } else {
        out.push({ type: 'text', content: decodeEntities(stripTags(inner)) });
      }
    } else if (m[4]) {
      out.push({ type: 'text', content: decodeEntities(m[4]) });
    }
  }
  return out;
}

function renderParsed(nodes: ParsedNode[]): ReactNode {
  return nodes.map((n, i) => {
    switch (n.type) {
      case 'text':
        return <Fragment key={i}>{n.content}</Fragment>;
      case 'br':
        return <br key={i} />;
      case 'span':
        return <span key={i}>{renderParsed(n.children)}</span>;
      case 'em':
        return <em key={i}>{renderParsed(n.children)}</em>;
      case 'strong':
        return <strong key={i}>{renderParsed(n.children)}</strong>;
      case 'b':
        return <b key={i}>{renderParsed(n.children)}</b>;
      case 'i':
        return <i key={i}>{renderParsed(n.children)}</i>;
      case 'u':
        return <u key={i}>{renderParsed(n.children)}</u>;
      case 'small':
        return <small key={i}>{renderParsed(n.children)}</small>;
      case 'mark':
        return <mark key={i}>{renderParsed(n.children)}</mark>;
      default:
        return null;
    }
  });
}


export { parseHtmlString, renderParsed, decodeEntities };
export type { ParsedNode, InlineTag };

/** Convenience: parse and render in one call. */
export function RichText({ text }: { text: string }) {
  return <>{renderParsed(parseHtmlString(text))}</>;
}
